import { useState } from "react";
import { ClassNamesConfig, GroupBase } from "react-select";
import AsyncSelect from "react-select/async";

import { ErrorMessage } from "@/app/components/error-message";
import { Label } from "@/components/ui/label";
import {
  SearchIndicator,
  createClassNames,
} from "@/components/ui/react-select";
import { getSessionRoomSystems } from "@/features/room-systems/api/get-room-systems";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { RoomSystemSelectOption } from "@/features/session/components/room-system-select-option";
import { RoomSystemOption } from "@/features/session/types/room-system-option";
import { queryClient } from "@/lib/react-query";
import { cn } from "@/lib/utils";
import { PublicEvent } from "@classview/shared-types";

import { useAddParticipantToEvent } from "../api/add-participant-to-event";
import { getEventQueryKey } from "../api/get-event";

import { RoomSystemsTable } from "./room-systems-table";

type RegisterRoomSystemToEventFormProps = {
  event: PublicEvent;
};

export function RegisterRoomSystemToEventForm({
  event,
}: RegisterRoomSystemToEventFormProps) {
  const { organisation } = useOrganisation();
  const [value, setValue] = useState("");
  const {
    mutateAsync: addRoomSystemsToEvent,
    isPending,
    isError,
    error,
    reset,
  } = useAddParticipantToEvent({
    onSuccess: (event) => {
      queryClient.setQueryData(getEventQueryKey(event.id), event);
    },
  });

  const reactSelectClassNames = createClassNames({
    control: () => {
      return cn(!!error && "border-destructive");
    },
  });

  const loadOptions = async (input: string): Promise<RoomSystemOption[]> => {
    const roomSystems = await getSessionRoomSystems({
      organisationId: organisation.id,
      search: input,
    });
    const filteredRoomSystems = roomSystems.items.filter(
      (roomSystem) =>
        !event.session.roomSystems.some((rs) => rs.id === roomSystem.id),
    );

    return filteredRoomSystems.map((roomSystem) => ({
      roomSystemId: roomSystem.id,
      label: roomSystem.name,
      id: roomSystem.id,
      name: roomSystem.name,
      organisationId: roomSystem.organisation.id,
      organisationName: roomSystem.organisation.name,
      conflictingSessions: roomSystem.sessions?.conflictingSessions || [],
    }));
  };

  const handleSubmit = async (roomSystem: RoomSystemOption) => {
    await addRoomSystemsToEvent({
      orgId: organisation.id,
      eventId: event.id,
      payload: {
        participants: [{ roomSystemId: roomSystem.id }],
      },
    });

    reset();
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Label>Add Room System</Label>
        <AsyncSelect<RoomSystemOption>
          unstyled
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base) => ({
              ...base,
              pointerEvents: "auto",
              zIndex: 100,
            }),
          }}
          components={{
            DropdownIndicator: SearchIndicator,
            ClearIndicator: () => null,
            Option: RoomSystemSelectOption,
            IndicatorSeparator: () => null,
          }}
          classNames={
            reactSelectClassNames as ClassNamesConfig<
              RoomSystemOption,
              false,
              GroupBase<RoomSystemOption>
            >
          }
          placeholder="Type room system name"
          noOptionsMessage={() => "Type room system name"}
          loadOptions={loadOptions}
          isDisabled={isPending}
          inputValue={value}
          onInputChange={(value: string) => {
            setValue(value);
          }}
          value={null}
          onChange={(value) => {
            if (value) {
              handleSubmit(value);
            }
          }}
        />
      </div>

      {event.session.roomSystems.length > 0 ? (
        <RoomSystemsTable
          event={event}
          isOrganisationFieldShown={false}
          roomSystems={event.session.roomSystems}
        />
      ) : (
        <p className="text-center text-sm text-muted-foreground">
          No room systems added yet.
        </p>
      )}

      {isError && <ErrorMessage error={error} />}
    </div>
  );
}
